/**
 * Location section — 3 variants (map_left, map_right, map_full)
 *
 * 地図表示は Google Maps の埋め込み iframe を使用。
 * - mapEmbedUrl が指定されていればそれを使用
 * - 指定なしなら address から自動生成（API キー不要、無料）
 *   形式: https://maps.google.com/maps?q={住所}&output=embed
 */
import type { LocationContent } from '../schemas/location';
import { Body, Eyebrow, H2, SectionFrame } from './primitives';

interface LP {
  content: LocationContent;
}

/**
 * 入力値を iframe の src として使える URL に正規化。
 * - <iframe ... src="..."> コード全体が貼られた場合は src 属性を抽出
 * - URL がそのまま貼られた場合はそのまま返す
 * - 空文字 / 抽出失敗時は空文字を返す
 */
function normalizeMapEmbedUrl(input: string): string {
  const trimmed = input.trim();
  if (!trimmed) return '';
  // <iframe ... src="..." ...> 形式から src を抽出（シングル/ダブルクォート両対応）
  const iframeMatch = trimmed.match(/<iframe[^>]*\bsrc=["']([^"']+)["']/i);
  if (iframeMatch) return iframeMatch[1];
  // src="..." 単独でも許容（タグなし）
  const srcOnlyMatch = trimmed.match(/^src=["']([^"']+)["']$/i);
  if (srcOnlyMatch) return srcOnlyMatch[1];
  return trimmed;
}

function buildMapUrl(address: string, override?: string | null): string {
  if (override) {
    const normalized = normalizeMapEmbedUrl(override);
    if (normalized) return normalized;
  }
  return `https://maps.google.com/maps?q=${encodeURIComponent(address)}&hl=ja&output=embed`;
}

function MapFrame({
  src,
  title,
  aspectRatio = '16 / 10',
  borderRadius = 'var(--ls-radius-lg)',
}: {
  src: string;
  title: string;
  aspectRatio?: string;
  borderRadius?: string;
}) {
  return (
    <div
      style={{
        position: 'relative',
        width: '100%',
        aspectRatio,
        borderRadius,
        overflow: 'hidden',
        border: '1px solid var(--ls-border)',
        background: 'var(--ls-surface-alt)',
      }}
    >
      <iframe
        src={src}
        title={title}
        loading="lazy"
        referrerPolicy="no-referrer-when-downgrade"
        style={{
          position: 'absolute',
          inset: 0,
          width: '100%',
          height: '100%',
          border: 0,
        }}
      />
    </div>
  );
}

function AddressBlock({
  eyebrow,
  headline,
  address,
  notes,
  addressLabel,
  accessLabel,
}: {
  eyebrow?: string | null;
  headline?: string | null;
  address: string;
  notes?: string | null;
  addressLabel?: string | null;
  accessLabel?: string | null;
}) {
  const addrLbl = addressLabel ?? '住所';
  const accLbl = accessLabel ?? 'アクセス';
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      {eyebrow && <Eyebrow>{eyebrow}</Eyebrow>}
      {headline && <H2 style={{ whiteSpace: 'pre-wrap' }}>{headline}</H2>}
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          gap: 8,
          padding: 20,
          border: '1px solid var(--ls-border)',
          borderRadius: 'var(--ls-radius-md)',
          background: 'var(--ls-surface)',
        }}
      >
        <div
          style={{
            fontSize: '0.7rem',
            fontWeight: 600,
            letterSpacing: '0.08em',
            textTransform: 'uppercase',
            color: 'var(--ls-text-muted)',
          }}
        >
          {addrLbl}
        </div>
        <Body style={{ whiteSpace: 'pre-wrap', margin: 0 }}>{address}</Body>
      </div>
      {notes && (
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            gap: 8,
            padding: 20,
            border: '1px dashed var(--ls-border)',
            borderRadius: 'var(--ls-radius-md)',
          }}
        >
          <div
            style={{
              fontSize: '0.7rem',
              fontWeight: 600,
              letterSpacing: '0.08em',
              textTransform: 'uppercase',
              color: 'var(--ls-text-muted)',
            }}
          >
            {accLbl}
          </div>
          <Body style={{ whiteSpace: 'pre-wrap', margin: 0 }}>{notes}</Body>
        </div>
      )}
    </div>
  );
}

/** 地図 左 / 住所 右 */
export function LocationMapLeft({ content }: LP) {
  const mapUrl = buildMapUrl(content.address, content.mapEmbedUrl);
  return (
    <SectionFrame>
      <div className="ls-split-pair" style={{ alignItems: 'start' }}>
        <div className="ls-split-media">
          <MapFrame src={mapUrl} title={content.headline ?? '地図'} />
        </div>
        <AddressBlock
          eyebrow={content.eyebrow}
          headline={content.headline}
          address={content.address}
          notes={content.notes}
          addressLabel={content.addressLabel}
          accessLabel={content.accessLabel}
        />
      </div>
    </SectionFrame>
  );
}

/** 住所 左 / 地図 右 */
export function LocationMapRight({ content }: LP) {
  const mapUrl = buildMapUrl(content.address, content.mapEmbedUrl);
  return (
    <SectionFrame>
      <div className="ls-split-pair" style={{ alignItems: 'start' }}>
        <AddressBlock
          eyebrow={content.eyebrow}
          headline={content.headline}
          address={content.address}
          notes={content.notes}
          addressLabel={content.addressLabel}
          accessLabel={content.accessLabel}
        />
        <div className="ls-split-media">
          <MapFrame src={mapUrl} title={content.headline ?? '地図'} />
        </div>
      </div>
    </SectionFrame>
  );
}

/** 大型地図 + 下に住所カード */
export function LocationMapFull({ content }: LP) {
  const mapUrl = buildMapUrl(content.address, content.mapEmbedUrl);
  return (
    <SectionFrame>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 32 }}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          {content.eyebrow && <Eyebrow>{content.eyebrow}</Eyebrow>}
          {content.headline && <H2 style={{ whiteSpace: 'pre-wrap' }}>{content.headline}</H2>}
        </div>
        <MapFrame src={mapUrl} title={content.headline ?? '地図'} aspectRatio="21 / 9" />
        <div
          className={content.notes ? 'ls-grid ls-grid-2' : 'ls-grid'}
          style={{ gap: 24 }}
        >
          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              gap: 8,
              padding: 20,
              border: '1px solid var(--ls-border)',
              borderRadius: 'var(--ls-radius-md)',
              background: 'var(--ls-surface)',
            }}
          >
            <div
              style={{
                fontSize: '0.7rem',
                fontWeight: 600,
                letterSpacing: '0.08em',
                textTransform: 'uppercase',
                color: 'var(--ls-text-muted)',
              }}
            >
              {content.addressLabel ?? '住所'}
            </div>
            <Body style={{ whiteSpace: 'pre-wrap', margin: 0 }}>{content.address}</Body>
          </div>
          {content.notes && (
            <div
              style={{
                display: 'flex',
                flexDirection: 'column',
                gap: 8,
                padding: 20,
                border: '1px dashed var(--ls-border)',
                borderRadius: 'var(--ls-radius-md)',
              }}
            >
              <div
                style={{
                  fontSize: '0.7rem',
                  fontWeight: 600,
                  letterSpacing: '0.08em',
                  textTransform: 'uppercase',
                  color: 'var(--ls-text-muted)',
                }}
              >
                {content.accessLabel ?? 'アクセス'}
              </div>
              <Body style={{ whiteSpace: 'pre-wrap', margin: 0 }}>{content.notes}</Body>
            </div>
          )}
        </div>
      </div>
    </SectionFrame>
  );
}

export const locationVariants = {
  map_left: LocationMapLeft,
  map_right: LocationMapRight,
  map_full: LocationMapFull,
};
